/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/sharedlib/src/data/common.data.ts
 */
import type { AdapterConfig, UserAuth } from '@repo/common';
import type { AxiosInstance } from 'axios';
import axios, { isAxiosError } from 'axios';
import axiosRetry from 'axios-retry';
import { userAuthModel } from '../stores/auth.store';
import { getApiEndpoint } from '../stores/config';
import constants from '../utils/constants';
import type { DataModel } from '../utils/store.util';

export const getClient = (signOut?: () => void) => {
  const endpoint = getApiEndpoint();

  const headers: Record<string, string> = {
    'X-CLIENT-ID': constants.CLIENT_ID,
    'X-CLIENT-VERSION': constants.PACKAGE_VERSION
  };
  if (typeof window === 'undefined') {
    // We only update the headers when requesting from the server
    headers['Origin'] = constants.API_SCHEME_HOST; // Manually set the Origin header
  }

  const apiClient = axios.create({
    baseURL: `${endpoint}/api`,
    withCredentials: true,
    headers
  });

  apiClient.interceptors.response.use(
    (response) => response, // Pass through successful responses
    (error) => {
      if (error.response?.status === 401 && error.response?.data?.error === 'token_expired') {
        // Trigger sign-out
        console.error('Token has expired');
        if (signOut) signOut(); // This will redirect to the sign-in page
      }
      return Promise.reject(error);
    }
  );

  return apiClient;
};

export const getProxyClient = () => {
  // constants.AI_PROXY_URL=https://aiproxy.retainapp.ai/api/v1
  if (!constants.RETAIN_SERVICE_KEY) {
    throw Error('Missing RETAIN_SERVICE_KEY');
  }
  const apiIndex = constants.AI_PROXY_URL.indexOf('/api');
  const endpoint =
    apiIndex !== -1 ? constants.AI_PROXY_URL.substring(0, apiIndex + 4) : constants.AI_PROXY_URL;

  return axios.create({
    withCredentials: true,
    baseURL: `${endpoint}`,
    headers: {
      Authorization: `Bearer ${constants.RETAIN_SERVICE_KEY}`,
      'X-CLIENT-ID': constants.CLIENT_ID,
      'Content-Type': 'application/json'
    }
  });
};

let ragClient: AxiosInstance | null = null;

export const getRagClient = (): AxiosInstance | null => {
  if (ragClient) {
    return ragClient;
  }
  const endpoint = constants.RAG_API_URL;
  if (!endpoint) {
    throw Error('Missing RAG_API_URL');
  }
  ragClient = axios.create({
    withCredentials: true,
    baseURL: `${endpoint}`,
    timeout: 60000, // 60 second timeout for RAG operations
    headers: {
      Authorization: `Bearer ${constants.RETAIN_SERVICE_KEY}`,
      'Content-Type': 'application/json',
      Connection: 'keep-alive'
    }
  });
  ragClient.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isAxiosError(error)) {
        if (error.code === 'ECONNREFUSED') {
          console.error(
            'Connection refused: The RAG service is not running. Please ensure the service is started and accessible.'
          );
        }
      }
      return Promise.reject(error);
    }
  );
  return ragClient;
};

export const getPrivateClient = (
  authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
  config?: AdapterConfig
) => {
  const refreshToken = authToken.refreshToken;
  const idToken = authToken.idToken;
  if (!refreshToken) {
    throw Error('No refresh token found');
  }
  if (!idToken || idToken.length < 10) {
    throw Error('No access token');
  }

  const endpoint = config?.baseUrl || getApiEndpoint();

  const client = axios.create({
    withCredentials: true,
    baseURL: `${endpoint}/api/private`,
    timeout: config?.timeout || 30000,
    headers: {
      Authorization: `Bearer ${idToken || ''}`,
      // TODO: Remove this once we have a client-side refresh endpoint
      'x-refresh-token': refreshToken,
      'X-CLIENT-ID': constants.CLIENT_ID,
      'X-CLIENT-VERSION': constants.PACKAGE_VERSION
    }
  });

  if (config?.retries) {
    axiosRetry(client, {
      retries: config.retries,
      retryDelay: (retryCount, error) => {
        // Handle rate limiting (429) with Retry-After header
        if (error.response?.status === 429) {
          // HTTP headers are case-insensitive, but 'retry-after' is the standard lowercase format
          const retryAfter =
            error.response.headers['retry-after'] || error.response.headers['Retry-After'];
          const retryDelay = retryAfter ? parseInt(retryAfter) * 1000 : 5000;
          console.log(
            `Rate limited. Retrying in ${retryDelay}ms (attempt ${retryCount}/${config.retries})`
          );
          return retryDelay;
        }
        // Use exponential backoff for other retryable errors
        return axiosRetry.exponentialDelay(retryCount);
      },
      retryCondition: (error) => {
        const status = error.response?.status;
        // Retry on rate limits (429) and server errors (5xx)
        return Boolean(status === 429 || (status && status >= 500 && status < 600));
      }
    });
  }

  // Add a response interceptor
  client.interceptors.response.use(
    async (response) => {
      const apiToken = response.headers['x-api-token'];
      const idToken = response.headers['x-access-token'];

      userAuthModel.update(($userAutModel: DataModel<UserAuth>) => {
        if (apiToken) {
          $userAutModel.item.apiToken = apiToken;
        }
        if (idToken) {
          $userAutModel.item.idToken = idToken;
        }
        return $userAutModel;
      });

      return response;
    },
    async (error) => {
      // Handle rate limiting (429) - let axios-retry handle the retry logic
      if (error.response && error.response.status === 429) {
        // HTTP headers are case-insensitive, but 'retry-after' is the standard lowercase format
        const retryAfter =
          error.response.headers['retry-after'] || error.response.headers['Retry-After'];
        const retryDelay = retryAfter ? parseInt(retryAfter) : 5;
        console.log(
          `Rate limited: ${error.config?.method?.toUpperCase()} ${error.config?.url} - retry after ${retryDelay}s`
        );
        // Let the error propagate to axios-retry for handling
        return Promise.reject(error);
      }

      // Handle 400 errors gracefully - log warning instead of throwing
      if (error.response && [422, 400].includes(error.response.status)) {
        const statusNumber = error.response.status;

        // For foreign key constraint errors, log more quietly since these are expected
        if (error.response.data?.code === 'FOREIGN_KEY_CONSTRAINT') {
          console.log(
            `Foreign key constraint for ${error.config?.method?.toUpperCase()} ${error.config?.url} - will retry in future sync`
          );
        } else {
          console.warn(
            `HTTP ${statusNumber} Bad Request: ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
            error.response.data || error.message
          );
        }

        // Return a mock successful response to prevent throwing
        return {
          data: {
            success: false,
            error: error.response.data?.error || 'Bad Request',
            status: statusNumber
          },
          status: 200, // Return 200 to prevent axios from treating this as an error
          statusText: 'OK',
          headers: error.response.headers,
          config: error.config
        };
      }

      // Handle 401 errors
      if (error.response && error.response.status === 401) {
        console.warn('Unauthorized. Token may have expired.');

        // TODO: Refresh the token
        const idToken = error.response.headers['x-access-token'];
        if (idToken) {
          console.log('Refreshing access token');
          userAuthModel.update(($userAutModel: DataModel<UserAuth>) => ({
            ...$userAutModel,
            item: {
              ...$userAutModel.item,
              idToken,
              refreshToken
            }
          }));
        }

        // Optional: Retry logic if a new token is obtained
        const originalRequest = error.config;

        if (!originalRequest._retry) {
          originalRequest._retry = true;
          originalRequest.headers['Authorization'] = `Bearer ${idToken}`;
          return client(originalRequest); // Retry the original request
        }
      }
      // You can also handle other status codes or log general errors
      return Promise.reject(error);
    }
  );

  return client;
};
